import { STORAGE_KEY } from '../constants/generic';
import {
  getAppObj,
  safe,
  sessionStorageGetJson,
  sessionStorageSetJson
} from '../utils/utils';
import { getAppAuthTokens } from '../utils/tokens';

// The account service raises `gdprExplainStatusChange` when the GDPR archival deleted
// the member's documents and moved his approved profile back to TO_COMPLETE, and
// lowers it on the next update of that profile. The notice follows that flag alone:
// neither the profile status nor the documents on file decide it.
// A dismissal only silences the notice for the profile it was given on, within the
// authenticated session it was given in (see gdprNoticeDismissalOutlived).
export function isGdprNoticeVisible(member, dismissal) {
  return member?.gdprExplainStatusChange === true && member.id !== dismissal?.profileId;
}

// True when a dismissal was given in another authenticated session than `session`,
// i.e. it must not silence the notice any more.
export function gdprNoticeDismissalOutlived(dismissal, session) {
  return Boolean(dismissal) && dismissal.session !== session;
}

// FNV-1a: the session id is only ever compared, so only a fingerprint of it is kept.
function fingerprint(text) {
  let hash = 0x811c9dc5;

  for (let i = 0; i < text.length; i++) {
    hash ^= text.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }

  return hash.toString(16);
}

// Which authenticated session this tab is in. A Keycloak session is identified by the
// `sid` claim that keycloak-js exposes as `keycloak.sessionId`; it stays the same over
// token refreshes and page reloads and changes with every new login. A legacy token
// session (impersonation) carries no such identity: null, and its fresh start is reset
// explicitly where it begins (App.handleImpersonateToken).
export function currentAuthSession() {
  const { token, refreshToken } = getAppAuthTokens();
  const { keycloak } = getAppObj();

  if (token || refreshToken) return null;
  if (!keycloak?.authenticated || !keycloak.sessionId) return null;

  return fingerprint(keycloak.sessionId);
}

// The dismissal lives in the tab's sessionStorage: it survives navigation and F5 in
// that tab only (a new tab may show the notice again - agreed product scope), never in
// localStorage, and is never sent to the server.
export function readGdprNoticeDismissal() {
  const dismissal = sessionStorageGetJson(STORAGE_KEY.GDPR_NOTICE_DISMISSAL);

  return dismissal?.profileId ? dismissal : null;
}

export function saveGdprNoticeDismissal(dismissal) {
  sessionStorageSetJson(STORAGE_KEY.GDPR_NOTICE_DISMISSAL, dismissal);
}

export function clearGdprNoticeDismissal() {
  safe(() => sessionStorage.removeItem(STORAGE_KEY.GDPR_NOTICE_DISMISSAL));
}
